<template>
  <div v-if="tenderInfo" class="tender-summary-info-block spacey-40">
    <div class="spacey-16">
      <h3 class="text text-black m-0 fs-20 fw-7">
        {{ $t('General information for') }} ”{{ tenderInfo.name }}”
      </h3>
      <div class="info-row">
        <div v-for="(item, idx) in generalInfo" :key="idx" class="info-cell text-sm">
          <div class="text-secondary spacex-6 info-cell__title">
            {{ item.title }}
            <InfoTooltip v-if="item.info" small bottom>
              {{ item.info }}
            </InfoTooltip>
          </div>
          <span class="text-primary capitalize">{{ item.value || '—' }}</span>
        </div>
      </div>
    </div>
    <div class="spacey-16">
      <div v-if="tenderInfo.description" class="d-contents spacey-20">
        <h3 class="text text-black m-0 fs-20 fw-7">
          {{ $t('Description') }}
        </h3>
        <p class="info-block-description">
          {{ tenderInfo.description }}
        </p>
      </div>
    </div>
    <div v-if="tenderInfo.contact_person_responsible_user" class="spacey-16">
      <h3 class="text text-black m-0 fs-20 fw-7">
        {{ $t('Contact') }}
      </h3>
      <UserInfo :user="tenderInfo.contact_person_responsible_user" full avatar-size="48" />
    </div>
    <div class="spacey-20">
      <div class="info-header">
        <h3 class="text text-black m-0 fs-20 fw-7">
          {{ $t('Contractors') }}
        </h3>
        <Button
          size="small"
          left-icon="plus"
          type="secondary"
          @click="editTender(tenderInfo.id, 3)"
        >
          {{ $t('Add new contractor') }}
        </Button>
      </div>
      <CompaniesTable :companies="tenderInfo.offers" @resend="resendOffer" />
    </div>
    <div v-if="tenderInfo.files && tenderInfo.files.length" class="spacey-20">
      <div class="info-header">
        <h3 class="text text-black m-0 fs-20 fw-7">
          {{ $t('Attached files') }}
        </h3>
        <Button
          size="small"
          left-icon="pencil"
          type="secondary"
          @click="editTender(tenderInfo.id, 2)"
        >
          {{ $t('Edit files') }}
        </Button>
      </div>
      <FilesTable :files="tenderInfo.files" />
    </div>
  </div>
</template>

<script>
  import { mapActions, mapState } from 'vuex';
  import prettyDates from '@/utils/formatting/pretty-date';
  import UserInfo from '@/components/ui/UserInfo';
  import FilesTable from '@/containers/tender/tender-info/files-table/FilesTable';
  import get from 'lodash.get';
  import CompaniesTable from '@/components/tenders/CompaniesTable';
  import addSpaces from '@/utils/formatting/add-spaces';
  import InfoTooltip from '@/components/ui/InfoTooltip';
  import factory from '@/utils/api/factory';
  import parseError from '@/utils/formatting/parse-error';
  import Button from '@/components/ui-v2/Button';
  import editTender from '@/utils/mixins/edit-tender';

  export default {
    name: 'TenderInfo',
    components: { UserInfo, FilesTable, CompaniesTable, InfoTooltip, Button },
    mixins: [editTender],
    computed: {
      ...mapState('global', ['currencySymbols']),
      ...mapState({
        tenderInfo: (state) => {
          return state.tender.tenderInfo;
        },
        projectStates: (state) => state.global.project_states,
      }),
      tenderCurrency() {
        return this.currencySymbols[this.tenderInfo.currency];
      },
      generalInfo() {
        return [
          {
            title: this.$t('Last updated'),
            value: this.tenderInfo.updated_at ? prettyDates(this.tenderInfo.updated_at, true) : '',
          },
          {
            title: this.$t('Expected price'),
            value: `${this.tenderCurrency} ${addSpaces(this.tenderInfo.public_budget, false)}`,
          },
          {
            title: this.$t('Project stage'),
            value: this.projectStates[this.tenderInfo.project.state],
          },
          {
            title: this.$t('Internal budget'),
            info: this.$t('Only visible to you and your teammates'),
            value: `${this.tenderCurrency} ${addSpaces(this.tenderInfo.internal_budget, false)}`,
          },
          {
            title: this.$t('Offers deadline'),
            value: this.tenderInfo.offers_deadline_date
              ? prettyDates(this.tenderInfo.offers_deadline_date, false, false)
              : '',
          },
          {
            title: this.$t('Execution address'),
            value: this.tenderInfo.address,
          },
          {
            title: this.$t('Construction beginning date'),
            value: this.tenderInfo.project.construction_beginning_date
              ? prettyDates(this.tenderInfo.project.construction_beginning_date)
              : '',
          },
        ];
      },
    },
    methods: {
      ...mapActions('alert', ['showAlert']),
      prettyDates,
      get,
      addSpaces,
      async resendOffer(idx) {
        try {
          await factory(`tenders/${this.tenderInfo.id}/offers`).postInPath(`${idx}/resend`);
          this.showAlert({
            type: 'success',
            timeout: 5,
            text: `The offer has been resent`,
          });
        } catch (e) {
          console.error(e);
          this.showAlert({
            type: 'error',
            text: parseError(e),
          });
        }
      },
    },
  };
</script>
